/**
 * GTM-only Saleor fetch for SKU enrichment — isolated from PLP/carousel ProductsBySlugs.
 */

import {
  ProductsBySkusForGtmDocument,
  type SaleorProductsBySkusForGtmQuery,
  type SaleorProductsBySkusForGtmQueryVariables,
} from '@/graphql/saleor/api';
import { convertLocaleToApiParams } from '@/lib/configuration/api-params';
import { clients, query } from '@/lib/graphql/client';
import { devError, devLog } from '@/lib/utils/devLog';

export type GtmProductVariant = NonNullable<
  SaleorProductsBySkusForGtmQuery['productVariants']
>['edges'][number]['node'];

/**
 * Fetch product variants with GTM-relevant Saleor fields, preserving input SKU order.
 *
 * Example Input:
 *   skus: ['AB1234.56'], locale: 'us-en'
 *
 * Example Output:
 *   [{ sku: 'AB1234.56', product: { name: 'Chronomat', productType: { slug: 'watch' } }, ... }]
 */
export async function fetchProductsForGtmBySkus(
  skus: string[],
  locale: string
): Promise<GtmProductVariant[]> {
  const filteredSkus = skus.filter((sku) => !!sku && typeof sku === 'string');
  if (filteredSkus.length === 0) {
    return [];
  }

  const { channel, languageCode } = convertLocaleToApiParams(locale);

  devLog('[GTM] Fetching products for SKU enrichment:', {
    skuCount: filteredSkus.length,
    channel,
  });

  try {
    const { productVariants } = await query<
      SaleorProductsBySkusForGtmQuery,
      SaleorProductsBySkusForGtmQueryVariables
    >(clients.saleor, ProductsBySkusForGtmDocument, {
      skus: filteredSkus,
      channel,
      languageCode,
      first: filteredSkus.length,
    });

    const skuToVariant = new Map<string, GtmProductVariant>();
    for (const edge of productVariants?.edges ?? []) {
      const variant = edge.node;
      if (variant.sku) {
        skuToVariant.set(variant.sku.toUpperCase(), variant);
      }
    }

    return filteredSkus
      .map((sku) => skuToVariant.get(sku.toUpperCase()))
      .filter((variant): variant is GtmProductVariant => variant !== undefined);
  } catch (error) {
    devError('[GTM] Failed to fetch products for SKU enrichment:', {
      skuCount: filteredSkus.length,
      error: error instanceof Error ? error.message : error,
    });
    return [];
  }
}
